import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { clearOverlays } from 'ducks/overlays/slices';
import { useAppDispatch } from 'store/hooks';
import { mergeQuery } from 'utils/queries';

import { default_filters_context } from './defaults';
import { FiltersContextProps, Queries } from './types';

const Context = createContext<FiltersContextProps>({ ...default_filters_context });

export const FiltersContext: FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [filters_context, setFiltersContext] = useState<FiltersContextProps>({
    ...default_filters_context,
  });

  const updateQueries = useCallback((queries: Queries) => {
    setFiltersContext((prev) => ({ ...prev, queries: { ...prev.queries, ...queries } }));
  }, []);

  const updateFilters = useCallback((filters: Queries) => {
    setFiltersContext((prev) => ({ ...prev, filters: { ...prev.filters, ...filters } }));
  }, []);

  const onSubmit = useCallback(() => {
    const { filters } = filters_context;
    navigate(`${pathname}${mergeQuery(search, filters)}`);
    dispatch(clearOverlays());
  }, [dispatch, navigate, filters_context, pathname, search]);

  const value = useMemo(
    () => ({
      ...filters_context,
      updateFilters,
      updateQueries,
      onSubmit,
    }),
    [filters_context, updateFilters, updateQueries, onSubmit]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useFiltersContext = () => useContext(Context);
